import { useState, useEffect, useCallback } from 'react';

interface ComplianceScoreData {
  score: number | null;
  lastUpdated: string;
  source: 'template_alignment' | 'document_average' | 'sop' | 'enhanced_sop';
  details?: {
    templateAlignment?: number;
    documentAverage?: number;
    sopScore?: number;
    enhancedSopScore?: number;
  };
}

interface UseComplianceScoreReturn {
  complianceScore: number | null;
  complianceScoreData: ComplianceScoreData;
  updateComplianceScore: (data: Partial<ComplianceScoreData>) => void;
  calculateComplianceScore: (synthesisData: any, analysisResults: any) => number | null;
  isLoading: boolean;
  error: string | null;
}

// Global state to share across components
let globalComplianceState: ComplianceScoreData = {
  score: null,
  lastUpdated: new Date().toISOString(),
  source: 'document_average',
  details: {}
};

const listeners: Set<() => void> = new Set();

const notifyListeners = () => {
  listeners.forEach(listener => listener());
};

export const useComplianceScore = (): UseComplianceScoreReturn => {
  const [complianceScoreData, setComplianceScoreData] = useState<ComplianceScoreData>(globalComplianceState);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Subscribe to global state changes
  useEffect(() => {
    const listener = () => {
      setComplianceScoreData(globalComplianceState);
    };
    listeners.add(listener);
    
    return () => {
      listeners.delete(listener);
    };
  }, []);

  const updateComplianceScore = useCallback((data: Partial<ComplianceScoreData>) => {
    globalComplianceState = {
      ...globalComplianceState,
      ...data,
      lastUpdated: new Date().toISOString()
    };
    notifyListeners();
  }, []);

  const calculateComplianceScore = useCallback((synthesisData: any, analysisResults: any): number | null => {
    setIsLoading(true);
    setError(null);
    
    try {
      let score: number | null = null;
      let source: ComplianceScoreData['source'] = 'document_average';
      const details: ComplianceScoreData['details'] = {};

      // Priority 1: Template alignment from synthesis data
      if (synthesisData?.template_alignment?.compliance_score) {
        score = synthesisData.template_alignment.compliance_score;
        source = 'template_alignment';
        details.templateAlignment = score || undefined;
      }
      
      // Priority 2: Enhanced SOP score (if available)
      else if (analysisResults?.sop?.compliance_score && analysisResults.sop.enhanced) {
        score = analysisResults.sop.compliance_score;
        source = 'enhanced_sop';
        details.enhancedSopScore = score || undefined;
      }
      
      // Priority 3: Regular SOP score
      else if (analysisResults?.sop?.compliance_score) {
        score = analysisResults.sop.compliance_score;
        source = 'sop';
        details.sopScore = score || undefined;
      }
      
      // Priority 4: Calculate average from document analyses
      else if (analysisResults?.document_analyses?.length > 0) {
        const validScores = analysisResults.document_analyses
          .map((analysis: any) => analysis.template_compliance?.compliance_score)
          .filter((score: any) => score != null && !isNaN(score));
        
        if (validScores.length > 0) {
          score = Math.round(validScores.reduce((sum: number, score: number) => sum + score, 0) / validScores.length);
          source = 'document_average';
          details.documentAverage = score || undefined;
        }
      }

      // Update global state
      updateComplianceScore({
        score,
        source,
        details
      });

      return score;
    } catch (err) {
      setError('Failed to calculate compliance score');
      console.error('Error calculating compliance score:', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [updateComplianceScore]);

  return {
    complianceScore: complianceScoreData.score,
    complianceScoreData,
    updateComplianceScore,
    calculateComplianceScore,
    isLoading,
    error
  };
};

// Export helper functions for backward compatibility
export const getGlobalComplianceScore = (): number | null => {
  return globalComplianceState.score;
};

export const setGlobalComplianceScore = (score: number, source: ComplianceScoreData['source'], details?: ComplianceScoreData['details']) => {
  globalComplianceState = {
    score,
    source,
    details: details || {},
    lastUpdated: new Date().toISOString()
  };
  notifyListeners();
};